import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { inr } from "@/lib/api";
import { PageHeader, Panel, Loading, Pill } from "@/components/kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, X, Filter } from "lucide-react";

export default function Approvals() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ from_date: "", to_date: "", status: "" });
  const { data, isLoading } = useQuery({
    queryKey: ["approvals", filters],
    queryFn: async () => (await api.get("/approvals", { params: Object.fromEntries(Object.entries(filters).filter(([, x]) => x)) })).data,
    placeholderData: (p) => p,
  });
  const act = useMutation({
    mutationFn: async ({ id, action }) => (await api.patch(`/approvals/${id}`, { action })).data,
    onSuccess: (d) => { toast.success(`Approval ${d.status}`); qc.invalidateQueries({ queryKey: ["approvals"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Action failed"),
  });

  if (isLoading || !data) return <Loading label="Loading approvals…" />;

  return (
    <div>
      <PageHeader title="Approval Workflow" subtitle="No-code multi-stage approval chain (Sales → Finance Review → Finance Approval)" />
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 mb-4 flex flex-wrap items-end gap-3" data-testid="approvals-filter">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 mr-2"><Filter size={15} /> Filters</div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1">From Date</div>
          <Input type="date" className="h-9 w-40" data-testid="ap-filter-from" value={filters.from_date} onChange={(e) => setFilters((f) => ({ ...f, from_date: e.target.value }))} />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1">To Date</div>
          <Input type="date" className="h-9 w-40" data-testid="ap-filter-to" value={filters.to_date} onChange={(e) => setFilters((f) => ({ ...f, to_date: e.target.value }))} />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Status</div>
          <select className="h-9 rounded-md border border-slate-200 px-2.5 bg-white text-sm" data-testid="ap-filter-status"
            value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
            <option value="">All Status</option><option value="pending">Pending</option><option value="approved">Approved</option><option value="rejected">Rejected</option>
          </select>
        </div>
        {(filters.from_date || filters.to_date || filters.status) && (
          <Button variant="ghost" size="sm" className="gap-1 text-slate-500" data-testid="ap-filter-clear"
            onClick={() => setFilters({ from_date: "", to_date: "", status: "" })}><X size={14} /> Clear</Button>
        )}
      </div>
      <Panel testid="approvals-panel" subtitle={`${data.approvals.length} workflow items`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 text-xs uppercase tracking-wider border-b border-slate-100">
                <th className="py-2.5 pr-4">Scheme</th><th className="py-2.5 pr-4">Stage</th>
                <th className="py-2.5 pr-4">Requested By</th><th className="py-2.5 pr-4 text-right">Amount</th>
                <th className="py-2.5 pr-4">Status</th><th className="py-2.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.approvals.map((a, i) => (
                <tr key={a.id} className="drill-row border-b border-slate-50" data-testid={`approval-row-${i}`}>
                  <td className="py-3 pr-4 font-medium text-slate-800">{a.scheme_name}</td>
                  <td className="py-3 pr-4 text-slate-600">{a.stage}</td>
                  <td className="py-3 pr-4 text-slate-500 text-xs">{a.requested_by}</td>
                  <td className="py-3 pr-4 text-right font-semibold text-[#00529B]">{inr(a.amount)}</td>
                  <td className="py-3 pr-4"><Pill tone={a.status}>{a.status}</Pill></td>
                  <td className="py-3 text-right">
                    {a.status === "pending" && (
                      <div className="flex justify-end gap-1.5">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid={`approve-btn-${i}`}
                          onClick={() => act.mutate({ id: a.id, action: "approve" })}><Check size={14} /> Approve</Button>
                        <Button size="sm" variant="outline" className="gap-1 text-red-600 border-red-200" data-testid={`reject-btn-${i}`}
                          onClick={() => act.mutate({ id: a.id, action: "reject" })}><X size={14} /> Reject</Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
